import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import axios from "axios";

// normaliza una subvención: snake_case -> camelCase
const normSubvencion = (s) => ({
  id: s.id,
  nombre: s.nombre,
  descripcion: s.descripcion,
  numArchivos: Number(s.numArchivos ?? s.num_archivos ?? 0),
  estadoConvocatoria: s.estadoConvocatoria ?? s.estado_convocatoria ?? "ACTIVA",
});

const fileKey = (f) => `${f.name}__${f.size}__${f.lastModified}`;

/** slug por campo: espacios -> "_", elimina tildes y símbolos raros */
function slugCampo(v) {
  return (v ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")   // quita tildes
    .replace(/[^\w\s.-]/g, "")         // quita raros salvo . _ - y espacios
    .trim()
    .replace(/\s+/g, "_")              // espacios -> _
    .replace(/_+/g, "_")               // colapsa múltiples _
    .replace(/^[_-]+|[_-]+$/g, "")     // limpia extremos
    .slice(0, 60);                     // límite prudente
}

// admite coma o punto como separador decimal
function parseImporte(str) {
  if (typeof str !== "string") return null;
  const clean = str.replace(/\./g, "").replace(",", ".").trim();
  const n = Number(clean);
  return Number.isFinite(n) ? n : null;
}

const SubvencionesPublicas = () => {
  const [subvenciones, setSubvenciones] = useState([]);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [subvencionSeleccionada, setSubvencionSeleccionada] = useState(null);
  const [archivos, setArchivos] = useState([]);
  const [solicitadas, setSolicitadas] = useState([]);
  const [colegiadoId, setColegiadoId] = useState(null);
  const [estadoPorSubvencion, setEstadoPorSubvencion] = useState({});
  const [enviando, setEnviando] = useState(false);

  // campos extra requeridos
  const [nombreCongreso, setNombreCongreso] = useState("");
  const [tituloPonencia, setTituloPonencia] = useState("");
  const [autores, setAutores] = useState("");
  const [importeStr, setImporteStr] = useState("");

  const API = process.env.REACT_APP_API_URL;

  // 1) Cargar catálogo
  useEffect(() => {
    axios
      .get(`${API}/api/subvenciones`)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data.map(normSubvencion) : [];
        setSubvenciones(list);
      })
      .catch((err) => console.error("Error al cargar subvenciones", err));
  }, [API]);

  // 2) Perfil (ids solicitadas + colegiadoId)
  useEffect(() => {
    const cargarSubvencionesSolicitadas = async () => {
      const token = localStorage.getItem("token");
      try {
        const res = await axios.get(`${API}/colegiado/perfil`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const ids = res.data.subvencionIds || [];
        setSolicitadas(ids);
        setColegiadoId(res.data.codigo);
      } catch (err) {
        console.error("Error al cargar subvenciones solicitadas", err);
      }
    };
    cargarSubvencionesSolicitadas();
  }, [API]);

  // 3) Estados de MIS subvenciones (opcional para chip informativo)
  useEffect(() => {
    const cargarEstados = async () => {
      if (!colegiadoId) return;
      const token = localStorage.getItem("token");
      try {
        const res = await axios.get(`${API}/api/subvenciones/misSubvenciones`, {
          params: { colegiadoId },
          headers: { Authorization: `Bearer ${token}` },
        });
        const mapa = {};
        (res.data || []).forEach((it) => {
          mapa[it.subvencionId] = it.estado;
        });
        setEstadoPorSubvencion(mapa);
      } catch (e) {
        console.error("Error cargando estados de subvenciones", e);
      }
    };
    cargarEstados();
  }, [colegiadoId, API]);

  const abrirModal = (subv) => {
    if (!colegiadoId) return;
    if (subv.estadoConvocatoria === "FINALIZADA") return; // único bloqueo
    setSubvencionSeleccionada(subv);
    setArchivos([]);
    setNombreCongreso("");
    setTituloPonencia("");
    setAutores("");
    setImporteStr("");
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setSubvencionSeleccionada(null);
    setArchivos([]);
    setEnviando(false);
  };

  const quitarArchivo = (idx) => {
    setArchivos((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleEnviarSolicitud = async () => {
    if (!subvencionSeleccionada || !colegiadoId || enviando) return;

    const requeridos = Number(subvencionSeleccionada?.numArchivos ?? 0);
    const importeNum = parseImporte(importeStr);

    if (!nombreCongreso.trim() || !tituloPonencia.trim() || !autores.trim()) {
      alert("Rellena Nombre congreso, Título ponencia y Autores.");
      return;
    }
    if (importeNum === null || importeNum < 0) {
      alert("Introduce un Importe válido (ej.: 123,45).");
      return;
    }

    const tieneArchivosNecesarios =
      (requeridos === 0 ? archivos.length > 0 : archivos.length >= requeridos) &&
      archivos.length > 0;

    if (!tieneArchivosNecesarios) {
      alert(`Debes adjuntar al menos ${requeridos} archivo(s).`);
      return;
    }

    setEnviando(true);
    const token = localStorage.getItem("token");

    try {
      // base del nombre para archivos
      const base =
        `${colegiadoId}-` +
        `${slugCampo(nombreCongreso)}-` +
        `${slugCampo(tituloPonencia)}-` +
        `${slugCampo(autores)}-` +
        `${String(importeNum).replace(".", ",")}`;

      // 1) Subir archivos renombrados + metadatos (igual que ya tenías)
      //    Si quieres que sea "todo o nada", habría que mover esto a un endpoint único en el backend.
      for (let i = 0; i < archivos.length; i++) {
        const archivo = archivos[i];
        const ext = archivo.name.includes(".") ? "." + archivo.name.split(".").pop() : "";
        const nuevoNombre = `${base}-f${i + 1}${ext}`;

        let payloadFile = archivo;
        try {
          payloadFile = new File([archivo], nuevoNombre, { type: archivo.type });
        } catch (_) {
          // iOS/Safari fallback
        }

        const formData = new FormData();
        formData.append("file", payloadFile);
        formData.append("colegiadoId", String(colegiadoId));

        // metadatos por si los persistes en BD
        formData.append("nombreCongreso", nombreCongreso);
        formData.append("tituloPonencia", tituloPonencia);
        formData.append("autores", autores);
        formData.append("importe", String(importeNum));
        formData.append("subvencionId", String(subvencionSeleccionada.id));

        await axios.post(`${API}/api/documentos/subir`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      }

      // 2) Registrar SOLICITUD en la nueva tabla (permite múltiples)
      const body = {
        colegiadoId,
        subvencionId: subvencionSeleccionada.id,
        nombreCongreso,
        tituloPonencia,
        autores,
        importe: importeNum,
      };
      await axios.post(`${API}/api/subvenciones/solicitudes`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // 3) Refresco UI (simple)
      setSolicitadas((prev) =>
        prev.includes(subvencionSeleccionada.id)
          ? prev
          : [...prev, subvencionSeleccionada.id]
      );

      alert("Solicitud registrada y documentos subidos correctamente.");
      cerrarModal();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data || "Error al solicitar subvención.";
      alert(Array.isArray(msg) ? msg.join("\n") : String(msg));
      setEnviando(false);
    }
  };

  const requeridos = subvencionSeleccionada?.numArchivos ?? 0;
  const importeNum = parseImporte(importeStr);
  const camposOk =
    nombreCongreso.trim() &&
    tituloPonencia.trim() &&
    autores.trim() &&
    importeNum !== null &&
    importeNum >= 0;

  const puedeEnviar =
    (requeridos === 0 ? archivos.length > 0 : archivos.length >= requeridos) &&
    archivos.length > 0 &&
    camposOk &&
    !enviando;

  return (
    <Paper elevation={3} sx={{ padding: 2, margin: 2 }}>
      <Box p={3}>
        <Typography variant="h4" gutterBottom>
          Subvenciones disponibles
        </Typography>

        {subvenciones.length === 0 ? (
          <Typography>No hay subvenciones activas.</Typography>
        ) : (
          subvenciones.map((subv0) => {
            const subv = normSubvencion(subv0);
            const estado = estadoPorSubvencion[subv.id]; // opcional
            const esFinalizada = subv.estadoConvocatoria === "FINALIZADA";
            const bloqueado = esFinalizada; // único bloqueo

            return (
              <Paper key={subv.id} elevation={3} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6">
                  {subv.nombre}
                  <span className="ml-2 text-sm text-gray-500 font-normal">
                    {" "}
                    · Requiere {subv.numArchivos} archivo(s)
                  </span>
                  <span
                    className={`ml-3 text-xs px-2 py-0.5 rounded ${esFinalizada ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"
                      }`}
                  >
                    {esFinalizada ? "FINALIZADA" : "ACTIVA"}
                  </span>
                </Typography>

                <div
                  className="tiptap-rendered"
                  dangerouslySetInnerHTML={{ __html: subv.descripcion }}
                />

                {bloqueado ? (
                  <Box sx={{ mt: 1 }}>
                    <Button variant="outlined" size="small" disabled>
                      Finalizada
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    {estado && (
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${estado === "CONCEDIDA"
                            ? "bg-green-100 text-green-800"
                            : estado === "PENDIENTE"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                      >
                        Último estado: {estado}
                      </span>
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => abrirModal(subv)}
                    >
                      Solicitar
                    </Button>
                  </Box>
                )}
              </Paper>
            );
          })
        )}
      </Box>

      {/* MODAL */}
      <Dialog open={modalAbierto} onClose={cerrarModal} fullWidth>
        <DialogTitle>Solicitar: {subvencionSeleccionada?.nombre}</DialogTitle>
        <DialogContent dividers>
          <Typography mb={2}>
            Debes subir <strong>{requeridos}</strong> archivo(s) obligatorios y rellenar
            los siguientes datos:
          </Typography>

          <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: "1fr 1fr" }}>
            <TextField
              label="Nombre congreso"
              value={nombreCongreso}
              onChange={(e) => setNombreCongreso(e.target.value)}
              fullWidth
            />
            <TextField
              label="Título ponencia"
              value={tituloPonencia}
              onChange={(e) => setTituloPonencia(e.target.value)}
              fullWidth
            />
            <TextField
              label="Autores"
              value={autores}
              onChange={(e) => setAutores(e.target.value)}
              fullWidth
            />
            <TextField
              label="Importe (€)"
              value={importeStr}
              onChange={(e) => setImporteStr(e.target.value)}
              placeholder="ej.: 123,45"
              fullWidth
            />
          </Box>

          <Box mt={2}>
            <input
              key={modalAbierto ? "open" : "closed"}
              type="file"
              multiple
              onChange={(e) => {
                const nuevos = Array.from(e.target.files || []);
                setArchivos((prev) => {
                  const mapa = new Map(prev.map((f) => [fileKey(f), f]));
                  for (const f of nuevos) mapa.set(fileKey(f), f);
                  return Array.from(mapa.values());
                });
                e.target.value = "";
              }}
            />
          </Box>

          <Typography mt={1}>
            {requeridos === 0
              ? "No hay archivos obligatorios, puedes adjuntar los que necesites."
              : (
                <>
                  Debes subir <strong>{requeridos}</strong> archivo(s) obligatorios.
                </>
              )}
          </Typography>

          <Box mt={1}>
            {archivos.map((f, idx) => (
              <Box
                key={fileKey(f)}
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <Typography variant="body2" sx={{ flex: 1 }} title={f.name}>
                  {f.name} ({Math.round(f.size / 1024)} KB)
                </Typography>
                <Button size="small" onClick={() => quitarArchivo(idx)}>
                  Quitar
                </Button>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarModal} disabled={enviando}>Cancelar</Button>
          <Button variant="contained" disabled={!puedeEnviar} onClick={handleEnviarSolicitud}>
            {enviando ? "Enviando..." : "Enviar solicitud"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SubvencionesPublicas;
